/**
 * MBA Lab — pure state/markdown engine for the Compassionate Accountability
 * Planner. Structurally cloned from charter-model.js: no DOM/storage/
 * analytics here, never builds HTML, escapes markdown-structural characters
 * so untrusted text can't reshape the document. accountability-planner.js
 * consumes buildDocumentModel()/buildMarkdown() so the live preview,
 * download, copy, and print all come from one source.
 *
 * Safety screening is deterministic and checkbox-driven, never keyword NLP -
 * see safetyScreen(). This is the step-5 differentiator's engine: the tool
 * must help identify whether a situation is an individual behavior, a
 * management gap, or a system problem, never just help someone sound
 * empathetic.
 */

import { STEPS, SYSTEM_CAUSES, SAFETY_CATEGORIES } from './planner-protocol.js';

export const SCHEMA_NAME = 'mbalab.accountability-planner';
export const SCHEMA_VERSION = 1;

export const LIMITS = Object.freeze({
  shortField: 500,
  longField: 1500,
  // Sized in bytes (UTF-8, via TextEncoder below), but the character limits
  // above are enforced in UTF-16 code units (clampText uses String#slice) -
  // for CJK text, 1 character is 3 UTF-8 bytes but still only 1 UTF-16 unit,
  // so a plan filled to the documented character limits entirely in Chinese/
  // Japanese/Korean needs roughly 12 longField + 2 shortField fields worth of
  // 3-byte characters (~57KB) to fit, not 1-byte ASCII (~19KB). The original
  // 40KB budget only had headroom for the ASCII case.
  maxSerializedBytes: 150 * 1024,
});

/** draft-message-review repurposes the opening field for pasting an
 * already-written message for review, rather than drafting a new one -
 * audit finding, since the field kept its "Suggested opening" framing even
 * though the mode's own intro says "reviewing a message you've already
 * drafted." Both accountability-planner.js (legend/label/checks-header) and
 * buildDocumentModel below (the generated document's section heading) read
 * these, so the wording can't drift between what's on screen and what's
 * downloaded. Every other mode keeps the original "Suggested opening"
 * framing - only this one repurposes the field. */
export const REVIEW_MODE_OPENING_LEGEND = 'Your draft, before you send it';
export const REVIEW_MODE_OPENING_LABEL = 'Your message';
export const REVIEW_MODE_OPENING_CHECKS_LABEL = 'Your message, above';
export const REVIEW_MODE_OPENING_HEADING = 'Your message';

const SYSTEM_CAUSE_IDS = new Set(SYSTEM_CAUSES.map((c) => c.id));
const SAFETY_CATEGORY_IDS = new Set(SAFETY_CATEGORIES.map((c) => c.id));

function emptySafetyChecklist() {
  const checklist = {};
  for (const cat of SAFETY_CATEGORIES) checklist[cat.id] = false;
  return checklist;
}

export function createInitialState(modeId, modeData) {
  return {
    schema: SCHEMA_NAME,
    version: SCHEMA_VERSION,
    savedAt: null,
    modeId,
    safetyChecklist: emptySafetyChecklist(),
    regulated: false,
    facts: '',
    assumptions: '',
    standard: '',
    impact: '',
    suggestedOpening: modeData?.suggestedOpening || '',
    questions: modeData?.suggestedQuestions || '',
    systemCauses: [],
    systemCausesNotes: '',
    support: modeData?.suggestedSupport || '',
    commitments: '',
    reviewTiming: '',
    consequences: '',
    risks: '',
    conflictBreakdown: '',
    oneOnOneAgenda: '',
    rubricChecked: [],
  };
}

function clampText(value, maxLen) {
  return typeof value === 'string' ? value.slice(0, maxLen) : '';
}

const TEXT_FIELD_LIMITS = Object.freeze({
  facts: LIMITS.longField,
  assumptions: LIMITS.longField,
  standard: LIMITS.shortField,
  impact: LIMITS.longField,
  suggestedOpening: LIMITS.longField,
  questions: LIMITS.longField,
  systemCausesNotes: LIMITS.longField,
  support: LIMITS.longField,
  commitments: LIMITS.longField,
  reviewTiming: LIMITS.shortField,
  consequences: LIMITS.longField,
  risks: LIMITS.longField,
  conflictBreakdown: LIMITS.longField,
  oneOnOneAgenda: LIMITS.longField,
});

export function setTextField(state, field, value) {
  if (!Object.hasOwn(TEXT_FIELD_LIMITS, field)) return state;
  return { ...state, [field]: clampText(value, TEXT_FIELD_LIMITS[field]) };
}

/**
 * Blanks every free-text field (derived from TEXT_FIELD_LIMITS, so it can
 * never miss one added there later), keeping only structural/progress state -
 * mode, safety flags, system-cause selections, rubric checks, timestamps.
 * Used for the opt-in "remember on this device" persistent save: unlike the
 * default session-only autosave, that tier is meant to survive indefinitely,
 * so the actual drafted conversation text about a real person never sits in
 * localStorage long-term unless the user re-enters it on a later visit.
 */
export function stripDraftText(state) {
  const stripped = { ...state };
  for (const field of Object.keys(TEXT_FIELD_LIMITS)) {
    stripped[field] = '';
  }
  return stripped;
}

export function setRegulated(state, checked) {
  return { ...state, regulated: checked === true };
}

export function setSafetyFlag(state, categoryId, checked) {
  if (!SAFETY_CATEGORY_IDS.has(categoryId)) return state;
  return { ...state, safetyChecklist: { ...state.safetyChecklist, [categoryId]: checked === true } };
}

export function toggleSystemCause(state, causeId) {
  if (!SYSTEM_CAUSE_IDS.has(causeId)) return state;
  const has = state.systemCauses.includes(causeId);
  const systemCauses = has ? state.systemCauses.filter((id) => id !== causeId) : [...state.systemCauses, causeId];
  return { ...state, systemCauses };
}

export function toggleRubricItem(state, index) {
  if (!Number.isInteger(index) || index < 0) return state;
  const has = state.rubricChecked.includes(index);
  const rubricChecked = has ? state.rubricChecked.filter((i) => i !== index) : [...state.rubricChecked, index];
  return { ...state, rubricChecked };
}

/**
 * Idempotent set, not a toggle - for a checkbox's own onchange handler, which
 * an element can genuinely receive twice per click (this codebase's own
 * pattern wires both `input` and `change` to the same handler; every other
 * checkbox field here reads el.checked directly for exactly this reason).
 * toggleSystemCause() above is correct for a single logical toggle call, but
 * is wrong to wire to a DOM event that can double-fire - setting to the same
 * checked value twice must be a true no-op, which a toggle can never be.
 */
export function setSystemCause(state, causeId, checked) {
  if (!SYSTEM_CAUSE_IDS.has(causeId)) return state;
  const has = state.systemCauses.includes(causeId);
  if (checked === has) return state;
  const systemCauses = checked ? [...state.systemCauses, causeId] : state.systemCauses.filter((id) => id !== causeId);
  return { ...state, systemCauses };
}

/** Idempotent set, not a toggle - see setSystemCause's doc comment for why. */
export function setRubricItem(state, index, checked) {
  if (!Number.isInteger(index) || index < 0) return state;
  const has = state.rubricChecked.includes(index);
  if (checked === has) return state;
  const rubricChecked = checked ? [...state.rubricChecked, index] : state.rubricChecked.filter((i) => i !== index);
  return { ...state, rubricChecked };
}

/** Unconditional reset-and-reseed for the new mode - the UI layer is
 * responsible for confirming with the user before calling this, since it
 * discards the current draft (see accountability-planner.js's restore-bar-
 * style confirm on mode switch). */
export function setMode(state, modeId, modeData) {
  return createInitialState(modeId, modeData);
}

function isValidSafetyChecklist(value) {
  if (!value || typeof value !== 'object') return false;
  return SAFETY_CATEGORIES.every((cat) => typeof value[cat.id] === 'boolean');
}

/**
 * @param {(modeId: string) => number|null} [getRubricLength] - resolves the
 *   current mode's rubric length, e.g. `id => MODES_BY_ID.get(id)?.rubric?.length ?? null`.
 *   Optional (defaults to no upper bound) so this stays decoupled from
 *   planner-modes.js like isValidModeId/defaultModeId already are - the real
 *   caller (accountability-planner.js) supplies it. Without a bound here,
 *   systemCauses/safetyChecklist are validated against their fixed known-id
 *   sets but rubricChecked wasn't validated against anything at all - a
 *   hand-edited localStorage could set an index far past the real rubric's
 *   length and the UI would render something like "12 of 6 checked."
 */
export function sanitizeState(raw, isValidModeId, defaultModeId, getRubricLength) {
  const r = raw && typeof raw === 'object' ? raw : {};
  const modeId = typeof r.modeId === 'string' && isValidModeId(r.modeId) ? r.modeId : defaultModeId;

  const safetyChecklist = isValidSafetyChecklist(r.safetyChecklist) ? { ...r.safetyChecklist } : emptySafetyChecklist();
  // Never trust an unknown key riding along in a checklist object.
  const cleanSafetyChecklist = emptySafetyChecklist();
  for (const cat of SAFETY_CATEGORIES) cleanSafetyChecklist[cat.id] = safetyChecklist[cat.id] === true;

  const systemCauses = Array.isArray(r.systemCauses)
    ? [...new Set(r.systemCauses.filter((id) => SYSTEM_CAUSE_IDS.has(id)))]
    : [];

  const rubricLength = typeof getRubricLength === 'function' ? getRubricLength(modeId) : null;
  const rubricChecked = Array.isArray(r.rubricChecked)
    ? [...new Set(r.rubricChecked.filter((i) => Number.isInteger(i) && i >= 0 && (rubricLength == null || i < rubricLength)))]
    : [];

  const state = {
    schema: SCHEMA_NAME,
    version: SCHEMA_VERSION,
    savedAt: typeof r.savedAt === 'number' ? r.savedAt : null,
    modeId,
    safetyChecklist: cleanSafetyChecklist,
    regulated: r.regulated === true,
    systemCauses,
    systemCausesNotes: clampText(r.systemCausesNotes, TEXT_FIELD_LIMITS.systemCausesNotes),
    rubricChecked,
  };
  for (const field of Object.keys(TEXT_FIELD_LIMITS)) {
    if (field === 'systemCausesNotes') continue;
    state[field] = clampText(r[field], TEXT_FIELD_LIMITS[field]);
  }
  return state;
}

const MIGRATIONS = {
  // No prior schema version has ever shipped - see charter-model.js's
  // identical note. Proves the mechanism works before it's ever needed.
  0: (state) => ({ ...state, version: 1 }),
};

function migrate(rawState) {
  let state = rawState;
  let migrated = false;
  while (typeof state.version === 'number' && state.version < SCHEMA_VERSION && MIGRATIONS[state.version]) {
    state = MIGRATIONS[state.version](state);
    migrated = true;
  }
  return { state, migrated };
}

/** @returns {{status: 'ok'|'migrated'|'unsupported-newer'|'malformed', state: object|null, raw: string|null}} */
export function parseSavedState(raw, isValidModeId, defaultModeId, getRubricLength) {
  let parsed;
  let rawString;

  if (typeof raw === 'string') {
    rawString = raw;
    try {
      parsed = JSON.parse(raw);
    } catch {
      return { status: 'malformed', state: null, raw: rawString };
    }
  } else {
    parsed = raw;
    try {
      rawString = raw != null ? JSON.stringify(raw) : null;
    } catch {
      rawString = null;
    }
  }

  if (
    !parsed ||
    typeof parsed !== 'object' ||
    parsed.schema !== SCHEMA_NAME ||
    !Number.isInteger(parsed.version) ||
    parsed.version < 0
  ) {
    // Number.isInteger(NaN) is false, so this also rejects a corrupted NaN
    // version - without this, `NaN > SCHEMA_VERSION` and `MIGRATIONS[NaN]`
    // are both silently false/undefined, so a corrupted version would fall
    // through as if it were already current instead of being flagged.
    return { status: 'malformed', state: null, raw: rawString };
  }
  if (parsed.version > SCHEMA_VERSION) {
    return { status: 'unsupported-newer', state: null, raw: rawString };
  }

  const { state: migratedState, migrated } = migrate(parsed);
  return {
    status: migrated ? 'migrated' : 'ok',
    state: sanitizeState(migratedState, isValidModeId, defaultModeId, getRubricLength),
    raw: rawString,
  };
}

export function serializeForSave(state) {
  const json = JSON.stringify(state);
  if (typeof TextEncoder !== 'undefined' && new TextEncoder().encode(json).length > LIMITS.maxSerializedBytes) {
    return null;
  }
  return json;
}

/**
 * @param {object} [modeData] - the current mode's data (from planner-modes.js),
 *   used to tell an untouched auto-seeded opening/questions/support apart
 *   from the user's own edits - see hasRealUserContent()'s doc comment for
 *   why this matters. Optional for backward compatibility, but every real
 *   caller should pass it: without it, a brand-new plan reports "2 of 8 steps
 *   started" before the user has typed anything at all, in every mode that
 *   seeds opening/questions/support (10 of the 11 modes).
 */
export function countStepsStarted(state, modeData) {
  const openingIsUntouchedSeed = state.suggestedOpening === (modeData?.suggestedOpening || '');
  const questionsIsUntouchedSeed = state.questions === (modeData?.suggestedQuestions || '');
  const supportIsUntouchedSeed = state.support === (modeData?.suggestedSupport || '');

  let count = 0;
  if (state.regulated || Object.values(state.safetyChecklist).some(Boolean)) count++;
  if (state.facts || state.assumptions) count++;
  if (state.standard || state.impact) count++;
  if (!openingIsUntouchedSeed || !questionsIsUntouchedSeed) count++;
  if (state.systemCauses.length > 0 || state.systemCausesNotes) count++;
  if (!supportIsUntouchedSeed || state.commitments) count++;
  if (state.reviewTiming || state.consequences) count++;
  if (state.risks) count++;
  return count;
}

/**
 * Whether the user has actually typed/selected anything, as opposed to just
 * having the mode's auto-seeded suggestedOpening/questions/support sitting
 * untouched. countStepsStarted() alone isn't safe for "should we warn before
 * discarding this?" - every fresh plan already has those 3 fields pre-filled,
 * so it reads as "started" before the user does anything at all. This checks
 * every field EXCEPT those three for any content, and for those three
 * specifically compares against the current mode's own seed text - editing
 * the suggested opening still counts as real content, an untouched seed does
 * not.
 */
export function hasRealUserContent(state, modeData) {
  const openingIsUntouchedSeed = state.suggestedOpening === (modeData?.suggestedOpening || '');
  const questionsIsUntouchedSeed = state.questions === (modeData?.suggestedQuestions || '');
  const supportIsUntouchedSeed = state.support === (modeData?.suggestedSupport || '');

  return (
    state.regulated ||
    Object.values(state.safetyChecklist).some(Boolean) ||
    !!state.facts ||
    !!state.assumptions ||
    !!state.standard ||
    !!state.impact ||
    !openingIsUntouchedSeed ||
    !questionsIsUntouchedSeed ||
    state.systemCauses.length > 0 ||
    !!state.systemCausesNotes ||
    !supportIsUntouchedSeed ||
    !!state.commitments ||
    !!state.reviewTiming ||
    !!state.consequences ||
    !!state.risks ||
    !!state.conflictBreakdown ||
    !!state.oneOnOneAgenda ||
    state.rubricChecked.length > 0
  );
}

/**
 * Deterministic, checkbox-driven only - no keyword NLP, no false confidence.
 * @returns {{level: 'ok'|'narrowed'|'stop', guidance: string|null, disabledSections: string[]}}
 */
// 'stop' gates nearly the whole document (everything but the preparation
// checklist, which is where the guidance itself surfaces) - the two 'stop'
// categories are conceptually different (acute physical/mental-health danger
// vs. a process that must be entirely org-led) but share the same real
// consequence: none of steps 2-8 should be self-authored in this tool right
// now. 'narrowed' keeps the original, narrower gating - early fact-gathering
// and standard-naming (steps 1-6) is still genuinely useful prep work for
// those categories, only the DIY consequences/escalation is the problem.
const STOP_DISABLED_SECTIONS = Object.freeze([
  'opening', 'standardImpact', 'questions', 'systemCauses', 'support',
  'commitments', 'reviewTiming', 'consequences', 'risks', 'escalation',
  'conflictBreakdown', 'oneOnOneAgenda', 'rubric',
]);
const NARROWED_DISABLED_SECTIONS = Object.freeze(['consequences', 'escalation']);

export function safetyScreen(state) {
  const flagged = SAFETY_CATEGORIES.filter((cat) => state.safetyChecklist[cat.id]);
  if (flagged.length === 0) return { level: 'ok', guidance: null, disabledSections: [] };

  const hasStop = flagged.some((cat) => cat.level === 'stop');
  const labels = flagged.map((cat) => cat.label.toLowerCase()).join(', ');

  if (hasStop) {
    return {
      level: 'stop',
      guidance:
        `You flagged ${labels}. This isn't something to plan alone in this document - it needs to be led ` +
        'by the right people first: security or emergency services if anyone is in danger, HR and legal if ' +
        'this is a real restructuring, or professional support for a mental-health concern. Come back to ' +
        'prepare for the conversation itself once that\'s underway.',
      // Spread into a fresh array each call, not the shared frozen constant
      // itself - matches the pre-existing contract (a caller mutating its
      // own copy, e.g. .sort(), must never corrupt every future call).
      disabledSections: [...STOP_DISABLED_SECTIONS],
    };
  }

  return {
    level: 'narrowed',
    guidance:
      `You flagged ${labels}. Steps 1-6 can still help you prepare, but consequences and formal escalation ` +
      'for this situation should be defined together with HR or legal, not decided unilaterally here.',
    disabledSections: [...NARROWED_DISABLED_SECTIONS],
  };
}

function p(text) {
  return { type: 'p', text };
}
function list(items) {
  return { type: 'list', items };
}

/** The single structured representation both the live preview and
 * buildMarkdown() render from - text only, never HTML. */
export function buildDocumentModel(state, modeData) {
  const screen = safetyScreen(state);
  const disabled = new Set(screen.disabledSections);
  const sections = [];

  // Every gated section (any key in disabled) renders the same guidance
  // paragraph instead of its normal content - generic across all sections so
  // a 'stop'-level flag can't leave any section still DIY-able by accident,
  // which is exactly the bug this replaced (only 'consequences'/'escalation'
  // were ever checked, regardless of severity level).
  const pushSection = (key, title, blocks) => {
    sections.push({ key, title, blocks: disabled.has(key) ? [p(screen.guidance)] : blocks });
  };

  const prepItems = [
    'Reread your facts vs. assumptions before you start.',
    'Block enough uninterrupted time - don\'t start this if you\'ll be cut off.',
  ];
  if (state.regulated) prepItems.push('You\'ve taken a moment to regulate before this conversation.');
  if (screen.level !== 'ok') prepItems.push(`Safety check: ${screen.guidance}`);
  const prepBlocks = [list(prepItems)];
  // Facts/assumptions (step 2) aren't their own output section (matching the
  // plan's exact section list), but they still need to surface somewhere
  // rather than being silently collected and discarded - they're reference
  // notes for the preparation step, same as the regulated checkbox above.
  // Never gated - this is where the guidance itself is read.
  if (state.facts || state.assumptions) {
    prepBlocks.push(p(`Facts: ${state.facts || '[not yet noted]'}`));
    prepBlocks.push(p(`Assumptions to check: ${state.assumptions || '[not yet noted]'}`));
  }
  sections.push({ key: 'preparation', title: 'Preparation checklist', blocks: prepBlocks });

  pushSection(
    'opening',
    modeData?.rubric ? REVIEW_MODE_OPENING_HEADING : 'Suggested opening',
    [p(state.suggestedOpening || '[Draft your opening line]')]
  );

  pushSection('standardImpact', 'Standard & impact', [
    p(`Standard: ${state.standard || '[Name the specific standard]'}`),
    p(`Impact: ${state.impact || '[Name the impact]'}`),
  ]);

  pushSection('questions', 'Questions to ask', [p(state.questions || '[Questions that invite their perspective]')]);

  const causeLabels = state.systemCauses.map((id) => SYSTEM_CAUSES.find((c) => c.id === id)?.label).filter(Boolean);
  const systemCausesBlocks = [causeLabels.length ? list(causeLabels) : p('[No system causes selected yet]')];
  if (state.systemCausesNotes) systemCausesBlocks.push(p(state.systemCausesNotes));
  pushSection('systemCauses', 'Likely system causes to investigate', systemCausesBlocks);

  pushSection('support', 'Support to consider', [p(state.support || '[Support options to consider]')]);

  pushSection('commitments', 'Commitments & owners', [
    p(state.commitments || '[What each person commits to, and by when]'),
  ]);

  pushSection('reviewTiming', 'Review timing', [p(state.reviewTiming || '[When you\'ll check back in]')]);

  pushSection('consequences', 'Potential consequences', [
    p(state.consequences || '[What happens if nothing changes]'),
  ]);

  pushSection('risks', 'Risks & tradeoffs', [p(state.risks || '[What could go wrong with this approach]')]);

  pushSection('escalation', 'Escalation triggers', [
    list([
      'Formal HR involvement - once informal conversation hasn\'t resolved a real performance or conduct gap.',
      'Legal or compliance review - once the situation touches policy, contract, or regulatory exposure.',
      'Safety or security team - for any physical safety concern, however small it seems.',
      'Employee assistance or professional support - once the conversation surfaces something beyond a manager\'s role.',
    ]),
  ]);

  // Mode-specific extra sections - only present for the modes that define
  // them, and gated the same as every other section above.
  if (modeData?.conflictBreakdownPrompt) {
    pushSection('conflictBreakdown', 'Conflict breakdown', [
      p(modeData.conflictBreakdownPrompt),
      p(state.conflictBreakdown || '[Your notes]'),
    ]);
  }
  if (modeData?.agendaTopics) {
    pushSection('oneOnOneAgenda', 'One-on-one agenda', [
      list(modeData.agendaTopics),
      p(state.oneOnOneAgenda || '[Notes for this session]'),
    ]);
  }
  if (modeData?.rubric) {
    const checkedCount = state.rubricChecked.length;
    pushSection('rubric', 'Self-review rubric', [
      p(`${checkedCount} of ${modeData.rubric.length} checked.`),
      list(modeData.rubric.map((item, i) => `${state.rubricChecked.includes(i) ? '[x]' : '[ ]'} ${item}`)),
    ]);
  }

  return { modeId: state.modeId, safetyLevel: screen.level, sections };
}

/** Escapes markdown-structural characters - identical approach to
 * charter-model.js's escapeMarkdown, see its doc comment for why. */
export function escapeMarkdown(text) {
  const escaped = String(text)
    .replace(/\\/g, '\\\\')
    .replace(/([`*_{}[\]()#+!|<>~&])/g, '\\$1');
  return escaped
    // 0-3 leading spaces, not column 0 only - CommonMark allows an indented
    // block marker, so an anchored-at-0 rule let pasted indented bullets and
    // `---`/`===` through as live structure. See charter-model.js's copy.
    .replace(/^( {0,3})([-=>])/gm, '$1\\$2')
    .replace(/^( {0,3})(\d{1,9})\./gm, '$1$2\\.');
}

export function buildMarkdown(doc, modeLabel) {
  const lines = [];
  lines.push(`# ${escapeMarkdown(modeLabel || 'Accountability Plan')}`);
  lines.push('');

  for (const section of doc.sections) {
    lines.push(`## ${escapeMarkdown(section.title)}`);
    lines.push('');
    for (const block of section.blocks) {
      if (block.type === 'p') {
        lines.push(escapeMarkdown(block.text));
        lines.push('');
      } else if (block.type === 'list') {
        for (const item of block.items) lines.push(`- ${escapeMarkdown(item)}`);
        lines.push('');
      }
    }
  }

  lines.push('---');
  lines.push('*Built with the free MBA Lab Compassionate Accountability Planner - mbalab.net/tools/accountability-planner*');

  return lines.join('\n');
}
